import { createTheme } from '@stylexjs/stylex';
import { colors } from './colors.stylex';
import { productAccents } from './palette.stylex';
import type { QomponentsTheme } from '../constants';

const darkPalette = {
  organizerSaveStatus: '#7d8590',
  organizerLegacyInputBackground: '#151b23',
  organizerLegacyInputBorder: '#30363d',
  organizerContentMenuBackground: '#0d1117',
  organizerMarkdownPanelBorder: '#161b22',
  organizerDashboardTriggerText: '#e6ecf2',
  organizerDashboardTriggerHover: '#151b23',
  organizerPreviewBackground: '#21262d',

  captureSuggestionText: '#e6ecf2',
  headerViewOnlyText: '#b7b7b7',
  formGroupHeadingBackground: '#262c36',
  toolsDisabledBorder: '#484f58',
  toolPanelBorder: '#30363d',
  toolTableHeaderBorder: '#3d444d',
  toolTableRowHover: '#262c36',
  formulaUnselectedBackground: '#223249',

  capsuleModeBackground: 'var(--sq-black)',
  toolSelectedRow: '#1a3829',

  templateOutputBorder: 'oklch(44.6% 0.03 256.802)',
  templateBreakingBackground: 'color-mix(in oklab, oklch(27.9% 0.077 45.635) 30%, transparent)',

  vantageActiveButtonBackground: '#7849a9',
  vantageActiveButtonHover: '#7849a9',
  vantageActiveButtonPressed: '#7849a9',
  vantageActiveButtonBorder: '#262c36',
  vantageActiveButtonFocusBorder: '#262c36',
  vantageActiveButtonText: '#f0f6fc',
  agentBuilderTextColor: '#e6ecf2',
  agentBuilderColorScheme: 'dark',
  agentBuilderDateBorder: 'var(--sq-tool-btn-border)',
  agentBuilderDateActiveBorder: 'var(--theme-darker)',
  agentBuilderDateFocusShadow: 'inset 0 1px 2px 0 color-mix(in srgb, var(--theme-darker) 7.5%, transparent)',

  agentBuilderPaletteBackground: '#0d1117',
  agentBuilderCardBackground: '#21262d',
  agentBuilderCardHoverBackground: '#0d1117',
  agentBuilderCardIcon: '#ffffff',
  agentBuilderPickerBackground: '#0d1117',
  agentBuilderPickerBorder: '#223249',
  agentBuilderPlaceholderBackground: 'var(--sq-background-input)',
  agentBuilderPlaceholderHover: 'var(--sq-tool-btn-bg-hover)',
  agentBuilderEdgeBackground: 'var(--sq-background-input)',
  agentBuilderEdgeBorder: 'var(--sq-tool-btn-border)',
  agentBuilderEdgeForeground: 'var(--sq-text-color)',
  agentBuilderHelpHover: '#ffffff',

  agentBuilderNodeBackground: 'var(--sq-background-input)',
  agentBuilderNodeBorder: 'color-mix(in srgb, var(--sq-tool-btn-border) 65%, var(--sq-background-input))',
  agentBuilderNodeMuted: 'var(--sq-text-muted)',
  agentBuilderNodeText: 'var(--sq-text-color)',
  agentBuilderNodeMeta: 'var(--sq-gray-text)',
  agentBuilderHandleLabel: 'var(--sq-text-muted)',
  agentBuilderBadgeBorder: 'color-mix(in srgb, var(--sq-tool-btn-border) 65%, var(--sq-background-input))',

  treeBuilderTabHover: colors.accentHighlightBackground,
  treeBuilderAssistantBackground: 'var(--sq-brand-header)',
  treeBuilderCardSelectedBackground: 'var(--sq-tools-background)',
  vantageRowHover: '#1c2128',
  contextLabelBackground: '#9198a1',
  vantageSettingsHover: '#223249',
  managementPageBackground: '#21262d',
  vantageWarningBackground: '#664d03',
  vantageWarningBorder: '#997404',
  vantageStandardBorder: '#30363d',
  vantageToolbarBackground: '#151b23',
  vantageChartWarningBackground: '#262c36',
  vantageChartWarningText: '#f0f6fc',
  contextFlagIcon: '#c77a7b',
  suppressionComparatorBorder: '#262c36',
  suppressionComparatorHover: '#0d1117',
  suppressionComparatorPressed: '#0d1117',
  suppressionComparatorSelectedText: '#f0f6fc',
  suppressionComparatorFocusBorder: '#262c36',
  suppressionComparatorSelected: productAccents.vantageLight,
  suppressionComparatorSelectedHover: productAccents.vantageLight,
  suppressionComparatorSelectedPressed: productAccents.vantageLight,
  suppressionComparatorSelectedBorder: '#262c36',
  suppressionComparatorSelectedHoverBorder: '#262c36',
  suppressionComparatorSelectedPressedBorder: '#262c36',
  workspaceSurface: '#151b23',
  workspaceBorder: 'rgb(255 255 255 / 15%)',
  workspaceFrameBorder: 'rgb(38 44 54 / 85%)',
  workspaceRowBorder: 'rgb(38 44 54 / 85%)',
  workspaceIcon: '#357992',
  agentVersionMenuBorder: 'currentColor',
  agentVersionMenuBackground: '#1f1f1f',
  assistantPanelBorder: '#161b22',
  legacyToolsGroupBorder: '#161b22',
  gaugeTrack: '#21262d',
  composerPlaceholder: '#7d8590',
  composerActionBackground: '#000000',
  subduedText: '#6e7681',
  effortTrack: '#151b23',
  effortSelected: '#30363d',
  effortHover: '#262c36',
  inputBackground: 'rgb(36, 37, 37)',
  diffAdded: 'oklch(39.3% 0.095 152.535)',
  diffRemoved: 'oklch(39.6% 0.141 25.723)',
  historyNumber: 'oklch(27.8% 0.033 256.848)',
  historyNumberText: '#000000',

  selectedFilterBackground: '#262c36',
  popoverHeading: '#262c36',
  separator: '#30363d',
  listRowAlternate: 'rgb(36, 45, 49)',
  readOnlyBackground: '#21262d',
  readOnlyBorder: '#484f58',
  readOnlyText: '#484f58',
  subtleBorder: '#484f58',
  sidebarBorder: '#30363d',
  sidebarBackground: '#151b23',
  sidebarHover: '#262c36',
  sidebarSelected: '#1c3b5c',
  mutedForeground: '#7d8590',
  supportingForeground: '#9198a1',
  controlHoverBackground: '#262c36',
  contrastForeground: '#ffffff',
  secondaryBackground: 'rgb(39, 40, 40)',
  contrastSelectedBackground: 'rgb(255 255 255 / 15%)',
  secondaryForeground: 'oklch(70.4% 0.04 256.788)',
  dashboardTileBorder: '#606061',
  errorIconBackground: 'oklch(63.7% 0.237 25.331 / 20%)',
  errorIconBorder: 'oklch(63.7% 0.237 25.331 / 30%)',
  assistantActionBorder: 'oklch(42.4% 0.199 265.638)',
  assistantActionStart: 'oklch(28.2% 0.091 267.935 / 30%)',
  assistantActionEnd: 'oklch(37.9% 0.146 265.522 / 30%)',
  assistantActionText: 'oklch(70.7% 0.165 254.624)',
  overlayPanelBackground: 'rgb(20 22 26 / 15%)',
  overlayPanelBorder: 'rgb(255 255 255 / 12%)',
  overlayPanelShadow: 'rgb(0 0 0 / 36%)',
  overlayPanelHighlight: 'rgb(255 255 255 / 8%)',
  boardTileBackground: '#151b28',
  boardTileBorder: 'rgb(255 255 255 / 10%)',
  boardTileShadow: 'rgb(0 0 0 / 40%)',
  boardTileLoadingOverlay: 'rgb(13 17 23 / 45%)',
  menuActionDisabled: '#484f58',
  checkboxBorder: '#e6ecf2',
  divider: '#30363d',
  surfaceSubtle: '#262c36',
  tableRowAlternate: '#151b23',
  checkboxImage:
    "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='%23c2bcb0' d='M12.207 4.793a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0l-2-2a1 1 0 011.414-1.414L6.5 9.086l4.293-4.293a1 1 0 011.414 0z'/%3e%3c/svg%3e\")",
  checkboxDisabledImage:
    "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='%23606061' d='M12.207 4.793a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0l-2-2a1 1 0 011.414-1.414L6.5 9.086l4.293-4.293a1 1 0 011.414 0z'/%3e%3c/svg%3e\")",
  checkboxIndeterminateImage:
    "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath stroke='%23c2bcb0' stroke-width='2' stroke-linecap='round' d='M4 8h8'/%3e%3c/svg%3e\")",
  checkboxDisabledIndeterminateImage:
    "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath stroke='%23606061' stroke-width='2' stroke-linecap='round' d='M4 8h8'/%3e%3c/svg%3e\")",
  modalBorder: 'oklch(55.1% 0.027 264.364)',
  tabBorder: 'oklch(37.3% 0.034 259.733)',
  tabHover: 'oklch(37.3% 0.034 259.733)',
  tabLabelActive: '#e6ecf2',
  segmentBorderInactive: '#484f58',
  segmentBackground: '#151b23',
  segmentHover: '#262c36',
  toolbarHover: 'rgb(239 239 239 / 30%)',
  toolbarSelected: '#223249',
  toolbarSelectedPressed: 'rgb(34 50 73 / 30%)',
  alertBackground: '#262c36',
  warningText: '#262c36',
  badgeNewBackground: '#664d03',
  badgeNewText: '#fce7ce',
  badgePositiveBackground: '#1e4032',
  badgePositiveText: '#1fcfa6',
  badgePositiveLabelText: '#000000',
  badgeNeutralBackground: 'rgb(17 24 39 / 40%)',
  badgeErrorBackground: '#44190f',
  panelBorder: 'oklch(55.1% 0.027 264.364)',
  panelBackground: '#151b23',
  selectionBackground: '#262c36',
  selectionText: '#ffffff',
  trackBackground: '#223249',
  bodyText: '#f0f6fc',
  secondaryText: '#7d8590',
  swatchBorder: 'oklch(55.1% 0.027 264.364)',
  menuBorder: 'oklch(55.1% 0.027 264.364)',
  menuText: '#ffffff',
  menuHover: '#151b23',
  surfaceHover: '#151b23',
  surfaceSelected: '#262c36',
  surfacePressed: '#262c36',
  navigationTextActive: '#e6ecf2',
  iconMuted: '#606061',
  background: '#0d1117',
  fieldBackground: '#0d1117',
  backgroundDisabled: '#223249',
  border: '#223249',
  borderInvalid: '#d9534f',
  borderDisabled: '#223249',
  text: '#e6ecf2',
  textDisabled: 'rgb(230 236 242 / 30%)',
  placeholder: 'rgb(230 236 242 / 30%)',
};

const topicLight = createTheme(colors, {
  organizerDateGroupBackground: '#f1f5f7',
  headerBackground: '#2a5c84',
  legacyToolsPrimary: '#2a5c84',
  workbookPrimary: '#2a5c84',
  legacyToolsLink: '#2a5c84',
  legacyToolsRowHover: '#edf7f6',
  legacyToolsRowFocus: '#edf7f6',
  timelineItemKind: productAccents.topicSubtle,
  vantageActiveButtonFocusBorder: productAccents.topicLight,
  suppressionComparatorFocusBorder: productAccents.topicLight,
  suppressionComparatorSelected: productAccents.topicLight,
  suppressionComparatorSelectedHover: productAccents.topicHighlight,
  suppressionComparatorSelectedPressed: productAccents.topicDark,
  suppressionComparatorSelectedBorder: productAccents.topicLight,
  suppressionComparatorSelectedHoverBorder: productAccents.topicHighlight,
  suppressionComparatorSelectedPressedBorder: productAccents.topicDark,
  paginationSelected: productAccents.topicLight,
  tabLabel: productAccents.topicLight,
  segmentBorder: '#1f5f9f',
  segmentSelected: '#d9ecff',
  segmentSelectedHover: '#c7e3ff',
  alertBackground: `color-mix(in srgb, ${productAccents.topicHighlight} 10%, transparent)`,
  accent: productAccents.topicLight,
  accentHover: productAccents.topicHighlight,
  accentPressed: productAccents.topicDark,
  navigationTextActive: productAccents.topicLight,
  accentSubtle: productAccents.topicSubtle,
  accentHighlightBackground: productAccents.topicSubtle,
  link: productAccents.topicLight,
  icon: productAccents.topicIcon,
  iconForeground: productAccents.topicIcon,
  borderHover: productAccents.topicLight,
  borderFocus: productAccents.topicLight,
  borderActive: productAccents.topicLight,
});
const topicDark = createTheme(colors, {
  organizerDateGroupBackground: '#242d31',
  headerBackground: 'rgb(49, 80, 104)',
  legacyToolsPrimary: '#91b4cb',
  workbookPrimary: '#91b4cb',
  legacyToolsLink: '#91b4cb',
  legacyToolsRowHover: '#242d31',
  legacyToolsRowFocus: '#242d31',
  timelineItemKind: '#242d31',
  paginationSelected: productAccents.topicLight,
  tabLabel: productAccents.topicDark,
  segmentBorder: productAccents.topicHighlight,
  segmentSelected: 'color-mix(in srgb, #41a5e1 54%, #151b23)',
  segmentSelectedHover: 'color-mix(in srgb, #41a5e1 64%, #151b23)',
  accent: productAccents.topicLight,
  accentHover: productAccents.topicHighlight,
  accentPressed: productAccents.topicDark,
  accentSubtle: productAccents.topicSubtle,
  accentHighlightBackground: 'rgb(207 201 202 / 10%)',
  link: productAccents.topicLight,
  icon: productAccents.topicIcon,
  iconForeground: '#ffffff',
  ...darkPalette,
  borderHover: productAccents.topicDark,
  borderFocus: productAccents.topicDark,
  borderActive: productAccents.topicDark,
});
const analysisLight = createTheme(colors, {
  headerBackground: '#007960',
  legacyToolsPrimary: '#007960',
  workbookPrimary: '#007960',
  legacyToolsLink: '#007960',
  legacyToolsRowHover: '#edf7f6',
  legacyToolsRowFocus: '#edf7f6',
  timelineItemKind: productAccents.topicSubtle,
  vantageActiveButtonFocusBorder: productAccents.analysisLight,
  suppressionComparatorFocusBorder: productAccents.analysisLight,
  suppressionComparatorSelected: productAccents.analysisLight,
  suppressionComparatorSelectedHover: productAccents.analysisHighlight,
  suppressionComparatorSelectedPressed: productAccents.analysisDark,
  suppressionComparatorSelectedBorder: productAccents.analysisLight,
  suppressionComparatorSelectedHoverBorder: productAccents.analysisHighlight,
  suppressionComparatorSelectedPressedBorder: productAccents.analysisDark,
  workspaceRowBackground: productAccents.analysisSubtle,
  workspaceIcon: productAccents.analysisLight,
  paginationSelected: productAccents.analysisLight,
  tabLabel: productAccents.analysisLight,
  segmentBorder: productAccents.analysisLight,
  segmentSelected: productAccents.analysisSubtle,
  segmentSelectedHover: '#dff2ee',
  alertBackground: `color-mix(in srgb, ${productAccents.analysisHighlight} 10%, transparent)`,
  accent: productAccents.analysisLight,
  accentHover: productAccents.analysisHighlight,
  accentPressed: productAccents.analysisDark,
  navigationTextActive: productAccents.analysisLight,
  accentSubtle: productAccents.analysisSubtle,
  accentHighlightBackground: productAccents.analysisSubtle,
  link: productAccents.analysisLight,
  icon: productAccents.analysisIcon,
  iconForeground: productAccents.analysisIcon,
  borderHover: productAccents.analysisLight,
  borderFocus: productAccents.analysisLight,
  borderActive: productAccents.analysisLight,
});
const analysisDark = createTheme(colors, {
  headerBackground: 'rgb(21, 99, 81)',
  legacyToolsPrimary: '#1fcfa6',
  workbookPrimary: '#1fcfa6',
  legacyToolsLink: '#1fcfa6',
  legacyToolsRowHover: '#1a3829',
  legacyToolsRowFocus: '#1a3829',
  timelineItemKind: productAccents.topicSubtle,
  workspaceRowBackground: productAccents.analysisSubtle,
  paginationSelected: productAccents.analysisDark,
  tabLabel: productAccents.analysisDark,
  segmentBorder: productAccents.analysisHighlight,
  segmentSelected: `color-mix(in srgb, ${productAccents.analysisHighlight} 54%, #151b23)`,
  segmentSelectedHover: `color-mix(in srgb, ${productAccents.analysisHighlight} 64%, #151b23)`,
  accent: productAccents.analysisLight,
  accentHover: productAccents.analysisHighlight,
  accentPressed: productAccents.analysisDark,
  accentSubtle: productAccents.analysisSubtle,
  accentHighlightBackground: 'rgb(207 201 202 / 10%)',
  link: productAccents.analysisLight,
  icon: productAccents.analysisIcon,
  iconForeground: '#ffffff',
  ...darkPalette,
  borderHover: productAccents.analysisDark,
  borderFocus: productAccents.analysisDark,
  borderActive: productAccents.analysisDark,
});
const datalabLight = createTheme(colors, {
  headerBackground: '#f26722',
  legacyToolsPrimary: '#f26722',
  workbookPrimary: '#f26722',
  legacyToolsLink: '#f26722',
  legacyToolsRowHover: '#eef8f4',
  legacyToolsRowFocus: 'transparent',
  timelineItemKind: productAccents.topicSubtle,
  vantageActiveButtonFocusBorder: productAccents.datalabLight,
  suppressionComparatorFocusBorder: productAccents.datalabLight,
  suppressionComparatorSelected: productAccents.datalabLight,
  suppressionComparatorSelectedHover: productAccents.datalabHighlight,
  suppressionComparatorSelectedPressed: productAccents.datalabDark,
  suppressionComparatorSelectedBorder: productAccents.datalabLight,
  suppressionComparatorSelectedHoverBorder: productAccents.datalabHighlight,
  suppressionComparatorSelectedPressedBorder: productAccents.datalabDark,
  workspaceRowBackground: productAccents.datalabSubtle,
  workspaceIcon: productAccents.datalabLight,
  paginationSelected: productAccents.datalabLight,
  tabLabel: productAccents.datalabLight,
  segmentBorder: '#b95b22',
  segmentSelected: '#ffe6d6',
  segmentSelectedHover: '#ffd9bf',
  alertBackground: `color-mix(in srgb, ${productAccents.datalabHighlight} 10%, transparent)`,
  accent: productAccents.datalabLight,
  accentHover: productAccents.datalabHighlight,
  accentPressed: productAccents.datalabDark,
  navigationTextActive: productAccents.datalabLight,
  accentSubtle: productAccents.datalabSubtle,
  accentHighlightBackground: productAccents.datalabSubtle,
  link: productAccents.datalabLight,
  icon: productAccents.datalabIcon,
  iconForeground: productAccents.datalabIcon,
  borderHover: productAccents.datalabLight,
  borderFocus: productAccents.datalabLight,
  borderActive: productAccents.datalabLight,
});
const datalabDark = createTheme(colors, {
  headerBackground: '#f26722',
  legacyToolsPrimary: '#f26722',
  workbookPrimary: '#f26722',
  legacyToolsLink: '#f26722',
  legacyToolsRowHover: '#1a3829',
  legacyToolsRowFocus: 'transparent',
  timelineItemKind: productAccents.topicSubtle,
  workspaceRowBackground: productAccents.datalabSubtle,
  paginationSelected: productAccents.datalabDark,
  tabLabel: productAccents.datalabDark,
  segmentBorder: productAccents.datalabHighlight,
  segmentSelected: 'color-mix(in srgb, #e58a52 54%, #151b23)',
  segmentSelectedHover: 'color-mix(in srgb, #e58a52 64%, #151b23)',
  accent: productAccents.datalabLight,
  accentHover: productAccents.datalabHighlight,
  accentPressed: productAccents.datalabDark,
  accentSubtle: productAccents.datalabSubtle,
  accentHighlightBackground: 'rgb(38 44 54 / 50%)',
  link: productAccents.datalabLight,
  icon: productAccents.datalabIcon,
  iconForeground: '#ffffff',
  ...darkPalette,
  borderHover: productAccents.datalabDark,
  borderFocus: productAccents.datalabDark,
  borderActive: productAccents.datalabDark,
});
const vantageLight = createTheme(colors, {
  headerBackground: '#7849a9',
  legacyToolsPrimary: '#7849a9',
  workbookPrimary: '#7849a9',
  legacyToolsLink: '#a35fec',
  legacyToolsRowHover: '#eef8f4',
  legacyToolsRowFocus: 'transparent',
  timelineItemKind: productAccents.vantageSubtle,
  vantageActiveButtonFocusBorder: productAccents.vantageLight,
  suppressionComparatorFocusBorder: productAccents.vantageLight,
  suppressionComparatorSelected: productAccents.vantageLight,
  suppressionComparatorSelectedHover: productAccents.vantageHighlight,
  suppressionComparatorSelectedPressed: productAccents.vantageDark,
  suppressionComparatorSelectedBorder: productAccents.vantageLight,
  suppressionComparatorSelectedHoverBorder: productAccents.vantageHighlight,
  suppressionComparatorSelectedPressedBorder: productAccents.vantageDark,
  workspaceRowBackground: 'transparent',
  workspaceIcon: productAccents.vantageLight,
  paginationSelected: productAccents.vantageLight,
  tabLabel: productAccents.vantageLight,
  segmentBorder: productAccents.vantageLight,
  segmentSelected: productAccents.vantageSubtle,
  segmentSelectedHover: '#c5addd',
  alertBackground: `color-mix(in srgb, ${productAccents.vantageHighlight} 10%, transparent)`,
  accent: productAccents.vantageLight,
  accentHover: productAccents.vantageHighlight,
  accentPressed: productAccents.vantageDark,
  navigationTextActive: productAccents.vantageLink,
  accentSubtle: productAccents.vantageSubtle,
  accentHighlightBackground: productAccents.vantageSubtle,
  link: productAccents.vantageLink,
  icon: productAccents.vantageIcon,
  iconForeground: productAccents.vantageIcon,
  borderHover: productAccents.vantageLight,
  borderFocus: productAccents.vantageLight,
  borderActive: productAccents.vantageLight,
});
const vantageDark = createTheme(colors, {
  headerBackground: '#7849a9',
  legacyToolsPrimary: '#7849a9',
  workbookPrimary: '#7849a9',
  legacyToolsLink: '#a35fec',
  legacyToolsRowHover: '#1a3829',
  legacyToolsRowFocus: 'transparent',
  vantageSelectedTabIcon: '#ffffff',
  timelineItemKind: productAccents.vantageSubtle,
  workspaceRowBackground: 'transparent',
  paginationSelected: productAccents.vantageDark,
  tabLabel: productAccents.vantageDark,
  segmentBorder: productAccents.vantageHighlight,
  segmentSelected: `color-mix(in srgb, ${productAccents.vantageHighlight} 54%, #151b23)`,
  segmentSelectedHover: `color-mix(in srgb, ${productAccents.vantageHighlight} 64%, #151b23)`,
  accent: productAccents.vantageLight,
  accentHover: productAccents.vantageHighlight,
  accentPressed: productAccents.vantageDark,
  accentSubtle: productAccents.vantageSubtle,
  accentHighlightBackground: productAccents.vantageSubtle,
  link: productAccents.vantageLink,
  icon: productAccents.vantageIcon,
  iconForeground: '#ffffff',
  ...darkPalette,
  borderHover: productAccents.vantageDark,
  borderFocus: productAccents.vantageDark,
  borderActive: productAccents.vantageDark,
});
const insightsLight = createTheme(colors, {
  headerBackground: '#0076d1',
  legacyToolsPrimary: '#0076d1',
  workbookPrimary: '#0076d1',
  legacyToolsLink: '#0076d1',
  legacyToolsRowHover: '#eef8f4',
  legacyToolsRowFocus: 'transparent',
  timelineItemKind: productAccents.insightsSubtle,
  vantageActiveButtonFocusBorder: productAccents.insightsLight,
  suppressionComparatorFocusBorder: productAccents.insightsLight,
  suppressionComparatorSelected: productAccents.insightsLight,
  suppressionComparatorSelectedHover: productAccents.insightsHighlight,
  suppressionComparatorSelectedPressed: productAccents.insightsDark,
  suppressionComparatorSelectedBorder: productAccents.insightsLight,
  suppressionComparatorSelectedHoverBorder: productAccents.insightsHighlight,
  suppressionComparatorSelectedPressedBorder: productAccents.insightsDark,
  workspaceRowBackground: 'transparent',
  workspaceIcon: productAccents.insightsLight,
  paginationSelected: productAccents.insightsLight,
  tabLabel: productAccents.insightsLight,
  segmentBorder: productAccents.insightsLight,
  segmentSelected: productAccents.insightsSubtle,
  segmentSelectedHover: '#b8dcff',
  alertBackground: `color-mix(in srgb, ${productAccents.insightsHighlight} 10%, transparent)`,
  accent: productAccents.insightsLight,
  accentHover: productAccents.insightsHighlight,
  accentPressed: productAccents.insightsDark,
  navigationTextActive: productAccents.insightsLight,
  accentSubtle: productAccents.insightsSubtle,
  accentHighlightBackground: productAccents.insightsSubtle,
  link: productAccents.insightsLight,
  icon: productAccents.insightsIcon,
  iconForeground: productAccents.insightsIcon,
  borderHover: productAccents.insightsLight,
  borderFocus: productAccents.insightsLight,
  borderActive: productAccents.insightsLight,
});
const insightsDark = createTheme(colors, {
  headerBackground: '#0076d1',
  legacyToolsPrimary: '#0076d1',
  workbookPrimary: '#0076d1',
  legacyToolsLink: '#0076d1',
  legacyToolsRowHover: '#1a3829',
  legacyToolsRowFocus: 'transparent',
  timelineItemKind: productAccents.insightsSubtle,
  workspaceRowBackground: 'transparent',
  paginationSelected: productAccents.insightsDark,
  tabLabel: productAccents.insightsDark,
  segmentBorder: productAccents.insightsHighlight,
  segmentSelected: `color-mix(in srgb, ${productAccents.insightsHighlight} 54%, #151b23)`,
  segmentSelectedHover: `color-mix(in srgb, ${productAccents.insightsHighlight} 64%, #151b23)`,
  accent: productAccents.insightsLight,
  accentHover: productAccents.insightsHighlight,
  accentPressed: productAccents.insightsDark,
  accentSubtle: productAccents.insightsSubtle,
  accentHighlightBackground: productAccents.insightsSubtle,
  link: productAccents.insightsLight,
  icon: productAccents.insightsIcon,
  iconForeground: '#ffffff',
  ...darkPalette,
  borderHover: productAccents.insightsDark,
  borderFocus: productAccents.insightsDark,
  borderActive: productAccents.insightsDark,
});

/** Apply one complete product/mode theme at each boundary, including portal roots. */
export const themes = {
  topic: { light: topicLight, dark: topicDark },
  analysis: { light: analysisLight, dark: analysisDark },
  datalab: { light: datalabLight, dark: datalabDark },
  vantage: { light: vantageLight, dark: vantageDark },
  insights: { light: insightsLight, dark: insightsDark },
} satisfies Record<QomponentsTheme, Record<'light' | 'dark', ReturnType<typeof createTheme>>>;
